import {
  AggregateFunctionName,
  ApiReturnCampaign,
  DimensionSource,
  IntervalJSON,
  SelectionSchemaMeasurePoint,
  Source,
} from '@highimpact-frontend/api-types';
import { ErrorStatus } from '@highimpact/dashboard-ui';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import { TimeRangeUnionForm } from '@highimpact/time-interval-selector';
import { Autocomplete, TextField, useTheme } from '@mui/material';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDashboardHost } from '../../../../context/DashboardHostContext';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  ensureConfiguredTable,
  getIntervals,
  isValid,
  setElementIndependentSourcesFromResolvedSources,
  setIntervals,
} from '../../../../state/dashboard-element-operations';
import {
  makeAggregatedColumn,
  makeEntityPropertyColumn,
  setAggregationTableColumnsE,
} from '../../../../state/table-element-operations';
import { getDefaultIndependentDimensionKeys } from '../shared/buildWidgetDataSourceRequest';
import {
  buildIndependentDimensionSelectionFromElement,
  dimensionSelectionToAxisSources,
  resolveIndependentDimensionSelectionFromCatalog,
  syncIndependentDimensionSelectionFromCatalog,
} from '../shared/independentDimensionSelection';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigInvalidStatus,
  PlotConfigSection,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import { areAxisSourceSelectionsEqual } from '../shared/resolveSourcesFromCatalog';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

const AGGREGATION_TABLE_DIMENSION_KEYS =
  getDefaultIndependentDimensionKeys('aggregation_table');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const AGGREGATION_TABLE_DIMENSION_KEY = AGGREGATION_TABLE_DIMENSION_KEYS[0]!;

type AggFuncNameWithoutTake = Exclude<
  AggregateFunctionName,
  'take_first' | 'take_last' | 'quartiles' | 'weighted_average'
>;
type AggregationTableStat = Exclude<AggFuncNameWithoutTake, 'last_values'>;

const AGGREGATION_TYPES: AggregationTableStat[] = [
  'mean',
  'min',
  'max',
  'sum',
  'count',
  'first_quartile',
  'median',
  'third_quartile',
];

function readSelectedAggregationsFromConfig(
  element: MigratedPlotFormProps['config']
): AggregationTableStat[] {
  const rows = (
    element.properties as {
      config?: {
        type?: string;
        rows?: { columns: { type: string; transformation_type?: string }[] }[];
      };
    }
  )?.config?.rows;

  if (!rows?.length) {
    return [];
  }

  const selected = new Set<AggregationTableStat>();
  for (const row of rows) {
    for (const column of row.columns) {
      if (
        column.type === 'AggregatedColumn' &&
        column.transformation_type &&
        (AGGREGATION_TYPES as string[]).includes(column.transformation_type)
      ) {
        selected.add(column.transformation_type as AggregationTableStat);
      }
    }
  }

  return AGGREGATION_TYPES.filter((type) => selected.has(type));
}

function AggregationTableForm({
  config,
  project_id,
  site_id,
  campaigns,
  start_time,
  end_time,
  onSubmit,
  onChange,
}: MigratedPlotFormProps & { campaigns: ApiReturnCampaign[] }) {
  const host = useDashboardHost();
  const tableScope = useMemo(
    () => ({
      start_date: start_time.toISOString(),
      end_date: end_time.toISOString(),
    }),
    [start_time, end_time]
  );
  const ensureConfigured = useCallback(
    (element: typeof config) => ensureConfiguredTable(element, tableScope),
    [tableScope]
  );
  const scope = useMemo(() => ({ project_id, site_id }), [project_id, site_id]);
  const { loading, error, data } = useWidgetSourceSelection({
    widget: 'aggregation_table',
    scope,
  });

  const initialDimensionSelection = useMemo(
    () =>
      buildIndependentDimensionSelectionFromElement(
        data && data.response_type === 'dimensions' ? data : null,
        config,
        AGGREGATION_TABLE_DIMENSION_KEYS,
        (sources) => ({ [AGGREGATION_TABLE_DIMENSION_KEY]: [...sources] })
      ),
    [config, data]
  );

  const [dimensionSelection, setDimensionSelection] = useState<
    Record<string, DimensionSource[]>
  >(initialDimensionSelection);
  const dimensionSelectionRef = useRef(dimensionSelection);
  dimensionSelectionRef.current = dimensionSelection;

  const getAggregationTypePrettyName = (type: AggregateFunctionName) => {
    switch (type) {
      case 'mean':
        return 'Moyenne';
      case 'min':
        return 'Minimum';
      case 'max':
        return 'Maximum';
      case 'sum':
        return 'Somme';
      case 'count':
        return 'Nombre';
      case 'first_quartile':
        return 'Premier quartile';
      case 'median':
        return 'Médiane';
      case 'third_quartile':
        return 'Troisième quartile';
      default:
        return type;
    }
  };

  const [selectedAggregations, setSelectedAggregations] = useState<
    AggregationTableStat[]
  >(() => readSelectedAggregationsFromConfig(config));
  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));
  const theme = useTheme();

  useEffect(() => {
    setDimensionSelection(initialDimensionSelection);
  }, [initialDimensionSelection]);

  useEffect(() => {
    setSelectedAggregations(readSelectedAggregationsFromConfig(config));
  }, [config]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setDimensionSelection((previousSelection) => {
      const syncedSelection = syncIndependentDimensionSelectionFromCatalog(
        data,
        AGGREGATION_TABLE_DIMENSION_KEYS,
        previousSelection
      );

      return areAxisSourceSelectionsEqual(
        dimensionSelectionToAxisSources(previousSelection),
        dimensionSelectionToAxisSources(syncedSelection)
      )
        ? previousSelection
        : syncedSelection;
    });
  }, [data]);

  const buildNewRows = useCallback(
    (
      dataSources: SelectionSchemaMeasurePoint[],
      sourceLabels: readonly Source[],
      selectedAggregationValues: AggregationTableStat[]
    ) => {
      // Without selected statistics, do not emit incomplete AggregationRows
      // (EntityPropertyColumn-only rows break the measure request builder).
      if (selectedAggregationValues.length === 0 || dataSources.length === 0) {
        return [];
      }

      const differentiatingEntity = 'measure_type';

      return dataSources.map((_path, index) => {
        const entityColumn = makeEntityPropertyColumn(
          differentiatingEntity,
          index,
          'name'
        );

        return [
          entityColumn,
          ...selectedAggregationValues.map((aggregationType) =>
            makeAggregatedColumn(
              aggregationType,
              index,
              sourceLabels[index]?.label ?? `Source ${index + 1}`
            )
          ),
        ];
      });
    },
    []
  );

  const applySourcesAndColumns = useCallback(
    (
      nextSelection: Record<string, DimensionSource[]>,
      aggregationValues: AggregationTableStat[]
    ) => {
      const resolvedSelection = resolveIndependentDimensionSelectionFromCatalog(
        data,
        nextSelection,
        AGGREGATION_TABLE_DIMENSION_KEYS
      );
      const resolvedSources =
        resolvedSelection[AGGREGATION_TABLE_DIMENSION_KEY] ?? [];
      const selectionPaths = resolvedSources.map((source) => ({
        measure_point_id: source.measure_point_id,
        measure_type_id: source.measure_type_id,
      }));
      const withSources = setElementIndependentSourcesFromResolvedSources(
        resolvedSelection,
        AGGREGATION_TABLE_DIMENSION_KEYS,
        ensureConfigured(config)
      );
      const newRows = buildNewRows(
        selectionPaths,
        resolvedSources,
        aggregationValues
      );
      const newConfig = setAggregationTableColumnsE(withSources, newRows);
      setValidation(isValid(newConfig));
      onChange(newConfig);
    },
    [buildNewRows, config, data, ensureConfigured, onChange]
  );

  const handleAggregationTypesChange = (
    _: SyntheticEvent<Element, Event>,
    selection: AggregationTableStat[]
  ) => {
    setSelectedAggregations(selection);
    applySourcesAndColumns(dimensionSelection, selection);
  };

  const handleIntervalsUpdate = (intervals: IntervalJSON[]) => {
    const newConfig = setIntervals(intervals, ensureConfigured(config));
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount((count) => count + 1);
    const currentValidation = isValid(config);
    setValidation(currentValidation);
    if (currentValidation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
      return;
    }

    const messages = Object.values(currentValidation[1]).flatMap(
      (fieldErrors) => fieldErrors ?? []
    );
    if (messages.length > 0) {
      host.showNotification(messages.join('. '), 'error');
    }
  };

  const dataSourceErrors = validation?.[1]?.dataSource ?? [];
  const rowsErrors = validation?.[1]?.rows ?? [];
  const submitted = submitCount > 0;
  const dataSourceHasError: ErrorStatus = submitted
    ? { tag: 'Submited', errors: dataSourceErrors }
    : { tag: 'Not submitted' };
  const aggregationFieldHasError = submitted && rowsErrors.length > 0;

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialIndependentSelection={dimensionSelection}
        onIndependentDimensionSelect={(_dimensionKey, selected) => {
          const nextSelection = {
            ...dimensionSelectionRef.current,
            [AGGREGATION_TABLE_DIMENSION_KEY]: selected,
          };
          setDimensionSelection(nextSelection);
          applySourcesAndColumns(nextSelection, selectedAggregations);
        }}
        matchControlPanelStyle
      />

      <PlotConfigInvalidStatus error={dataSourceHasError} />

      <PlotConfigFormDivider />

      <PlotConfigSection title="Type d'agrégation">
        <Autocomplete
          multiple
          size="small"
          options={AGGREGATION_TYPES}
          getOptionLabel={getAggregationTypePrettyName}
          value={selectedAggregations}
          onChange={handleAggregationTypesChange}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Ajouter des agrégations"
              error={aggregationFieldHasError}
              helperText={
                aggregationFieldHasError ? rowsErrors.join('. ') : undefined
              }
            />
          )}
          slotProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-option[aria-selected="true"]': {
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: theme.palette.secondary.contrastText,
                },
                '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                  backgroundColor: `${theme.palette.primary.dark} !important`,
                },
              },
            },
          }}
        />
      </PlotConfigSection>

      <PlotConfigFormDivider />

      <TimeRangeUnionForm
        onChange={handleIntervalsUpdate}
        campaigns={campaigns}
        defaultIntervals={getIntervals(config)}
        matchControlPanelStyle
      />

      <PlotConfigSubmitButton
        isSending={isSending}
        loading={loading}
        disabled={
          submitted && (dataSourceErrors.length > 0 || rowsErrors.length > 0)
        }
      />
    </PlotConfigFormShell>
  );
}

export default AggregationTableForm;
